import {
  buildDrivingLabelCode,
  buildDrivingRouteGroupName,
  normalizeDrivingRouteGroupKey,
} from "@utils/mode_tool/modeRoute";
import { resolveMediaKind, resolveMediaUrl } from "@utils/media/mediaUrl";
import { isDirectionRoutePoint } from "@utils/mode_tool/validation";

function readEnglishValue(field) {
  if (typeof field === "string") return field;
  if (Array.isArray(field)) {
    const englishItem = field.find((item) =>
      item && Object.keys(item).some((language) => language.toLowerCase() === "en"),
    );
    const englishKey = englishItem && Object.keys(englishItem).find(
      (language) => language.toLowerCase() === "en",
    );
    const value = englishItem?.[englishKey];
    if (typeof value === "string") return value;
    return String(value?.value ?? "");
  }
  const englishKey = field && Object.keys(field).find(
    (language) => language.toLowerCase() === "en",
  );
  const value = field?.[englishKey];
  if (typeof value === "string") return value;
  return String(value?.value ?? field?.value ?? "");
}

export function isDirectionPoint(point = {}) {
  return isDirectionRoutePoint(point);
}

// Keep the old export for existing imports while Direction is now determined
// exclusively by a non-zero turn_case/turnCase, not by the localized point name.
export const isEnglishDirectionPoint = isDirectionPoint;

export function isMainDrivingRoutePoint(point = {}, index = 0) {
  return normalizeDrivingRouteGroupKey(
    point?.routeGroup ?? point?.route_group ?? "",
    point?.routeGroupName ?? point?.route_group_name ?? "",
    Number(point?.number_point ?? point?.order ?? index + 1),
  ) === "main";
}

function normalizeEmptyRichText(value) {
  const html = String(value ?? "");
  const visibleText = html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;|&#160;|&#x0*a0;/gi, " ")
    .replace(/&#8203;|&#x200b;/gi, "")
    .replace(/[\u00a0\u200b\s]/g, "")
    .trim();
  return visibleText ? html : "";
}

function normalizeAiAudioDescription(value) {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];
  return Object.entries(value).map(([language, entry]) => ({
    [language]: entry && typeof entry === "object" ? entry.value ?? "" : String(entry ?? ""),
    id: String(entry?.id || ""),
  }));
}

export function buildRoutePointAutomationPayload({
  tourId,
  routeType,
  routeRaw,
  points,
}) {
  const durationMinutesByPointId = new Map();
  if (String(routeType || "").trim().toLowerCase() === "driving") {
    Object.values(routeRaw?.routes || {}).forEach((route) => {
      (Array.isArray(route?.pointDurations) ? route.pointDurations : []).forEach(
        (row) => {
          const pointId = String(row?.pointId || "");
          const durationSeconds = Number(row?.durationSeconds);
          if (pointId && Number.isFinite(durationSeconds) && durationSeconds > 0) {
            durationMinutesByPointId.set(
              pointId,
              Math.max(1, Math.round(durationSeconds / 60)),
            );
          }
        },
      );
    });
  }

  return {
    tour_id: String(tourId || "").trim(),
    route_type: String(routeType || "").trim().toLowerCase(),
    directiosApiRoutePointRaw: routeRaw || null,
    points: (Array.isArray(points) ? points : []).map((point, index) => {
      const pointId = String(point?.id || `point-${index + 1}`);
      const targetDurationMinutes = durationMinutesByPointId.get(pointId);
      return {
        id: pointId,
        number_point: Number(point?.number_point ?? point?.order ?? index + 1) || index + 1,
        latitude: String(point?.latitude ?? point?.lat ?? ""),
        longitude: String(point?.longitude ?? point?.lng ?? ""),
        turn_case: Number(point?.turnCase ?? point?.turn_case ?? 0) || 0,
        ...(targetDurationMinutes
          ? { target_duration_minutes: targetDurationMinutes }
          : {}),
        name: readEnglishValue(point?.name),
        description: normalizeEmptyRichText(readEnglishValue(point?.description)),
        more_info: normalizeEmptyRichText(readEnglishValue(point?.more_info)),
        notes: String(point?.notes ?? point?.note ?? ""),
        radius_meters: Number(point?.radiusMeters ?? point?.radius_meters) > 0
          ? Number(point?.radiusMeters ?? point?.radius_meters)
          : null,
        route_group: normalizeDrivingRouteGroupKey(
          point?.routeGroup ?? point?.route_group ?? "",
          point?.routeGroupName ?? point?.route_group_name ?? "",
          Number(point?.number_point ?? point?.order ?? index + 1),
        ),
        route_group_name: String(
          point?.routeGroupName ?? point?.route_group_name ??
          buildDrivingRouteGroupName(
            Number(point?.number_point ?? point?.order ?? index + 1) || index + 1,
            point?.routeGroup ?? point?.route_group ?? "main",
          ),
        ),
        ai_audio_description: normalizeAiAudioDescription(
          point?.aiAudioDescription ?? point?.ai_audio_description,
        ),
        images: Array.isArray(point?.images) ? point.images : [],
        audios: Array.isArray(point?.audios) ? point.audios : [],
      };
    }),
  };
}

export function buildRoutePointAutomationPayloadForAction({
  tourId,
  routeType,
  routeRaw,
  points,
  action,
  tourName,
  routeName,
}) {
  const sourcePoints = Array.isArray(points) ? points : [];

  const payload = buildRoutePointAutomationPayload({
    tourId,
    routeType,
    routeRaw,
    points: sourcePoints,
  });

  if (action === "content") {
    return {
      ...payload,
      tour_name: String(tourName || "").trim(),
      route_name: String(routeName || "").trim(),
      points: payload.points.map((point) =>
        Number(point?.target_duration_minutes)
          ? point
          : { ...point, target_duration_minutes: 1 },
      ),
    };
  }
  if (action === "directions") {
    return {
      ...payload,
      points: payload.points.map((point, index) =>
        isDirectionPoint(sourcePoints[index]) &&
        !Number(point?.target_duration_minutes)
          ? { ...point, target_duration_minutes: 1 }
          : point,
      ),
    };
  }
  if (action === "images") {
    return {
      tour_id: String(tourId || "").trim(),
      route_type: String(routeType || "").trim().toUpperCase(),
      points: sourcePoints.map((point, index) => {
        const multimediaImages = (Array.isArray(point?.multimedia) ? point.multimedia : [])
          .filter((item) => resolveMediaKind(item) === "image")
          .map((item) => resolveMediaUrl(String(item?.url || "")))
          .filter(Boolean);
        const suppliedImages = Array.isArray(point?.images)
          ? point.images.map((image) => typeof image === "string" ? image : image?.url).filter(Boolean)
          : [];
        return {
          id: String(point?.id || `point-${index + 1}`),
          name: readEnglishValue(point?.name),
          description: readEnglishValue(point?.description),
          more_info: readEnglishValue(point?.more_info),
          turn_case: Number(point?.turnCase ?? point?.turn_case ?? 0) || 0,
          images: suppliedImages.length ? suppliedImages : multimediaImages,
        };
      }),
    };
  }
  if (action === "parking") {
    return {
      ...payload,
      points: payload.points
        .filter((point, index) => isMainDrivingRoutePoint(sourcePoints[index], index))
        .map((point) => ({ ...point, route_group: "main" })),
    };
  }
  return payload;
}

function mergeGeneratedImages(original, row) {
  if (!Array.isArray(row?.images)) return original?.multimedia;
  const multimedia = Array.isArray(original?.multimedia) ? [...original.multimedia] : [];
  const existingImages = multimedia.filter((item) => resolveMediaKind(item) === "image");
  const generatedId = String(row?.generated_image_id || "");
  row.images.forEach((image) => {
    const url = resolveMediaUrl(String(typeof image === "string" ? image : image?.url || ""));
    if (!url) return;
    const imageId = String((typeof image === "object" && image?.id) || generatedId || "");
    const alreadyPresent = existingImages.some((item) =>
      (imageId && String(item?.id || item?.sourceId || "") === imageId) ||
      resolveMediaUrl(String(item?.url || "")) === url,
    );
    if (alreadyPresent) return;
    multimedia.push({
      ...(typeof image === "object" ? image : {}),
      id: imageId,
      sourceId: imageId,
      url,
      mediaType: "image",
      ...(generatedId ? { generatedImageId: generatedId } : {}),
    });
  });
  return multimedia;
}

function localizedEnglishField(currentField, value) {
  if (typeof currentField === "string") {
    return { en: { value, id: "" } };
  }
  if (Array.isArray(currentField)) {
    const nextField = currentField.reduce((localized, item) => {
      if (!item || typeof item !== "object") return localized;
      const entry = Object.entries(item).find(([language]) => language !== "id");
      if (!entry) return localized;
      const [language, localizedValue] = entry;
      const normalizedLanguage = String(language || "").toLowerCase();
      const entryValue = localizedValue && typeof localizedValue === "object"
        ? localizedValue.value
        : localizedValue;
      localized[normalizedLanguage] = {
        value: normalizedLanguage === "en" ? value : String(entryValue ?? ""),
        id: String(item?.id || localizedValue?.id || ""),
      };
      return localized;
    }, {});
    if (!Object.prototype.hasOwnProperty.call(nextField, "en")) {
      nextField.en = { value, id: "" };
    }
    return nextField;
  }
  const field = currentField && typeof currentField === "object" ? currentField : {};
  const currentEnglish = field.en;
  return {
    ...field,
    en: currentEnglish && typeof currentEnglish === "object"
      ? { ...currentEnglish, value }
      : value,
  };
}

function toGeneratedRoutePoint(row, index, originalPoints, routeType) {
  const insertion = row?.insert_between || {};
  const fromPointId = String(insertion?.from_point_id || "");
  const toPointId = String(insertion?.to_point_id || "");
  const beforeOriginalArrayIndex = toPointId
    ? originalPoints.findIndex((point) => String(point?.id || "") === toPointId)
    : -1;
  const afterOriginalIndex = fromPointId
    ? originalPoints.findIndex((point) => String(point?.id || "") === fromPointId)
    : -1;
  const beforeOriginal = beforeOriginalArrayIndex >= 0
    ? originalPoints[beforeOriginalArrayIndex]
    : null;
  const afterOriginal = afterOriginalIndex >= 0
    ? originalPoints[afterOriginalIndex]
    : null;
  const indexedBeforeOriginalIndex = Number(insertion?.to_point_index);
  const indexedBeforeOriginal = !beforeOriginal && Number.isInteger(indexedBeforeOriginalIndex)
    ? originalPoints[indexedBeforeOriginalIndex]
    : null;
  // Parking's current contract anchors insertion after from_point_id. Use a
  // fractional sort order so the point reliably lands before the next point,
  // even when response rows and existing points have equal integer orders.
  const insertionOrder = afterOriginal
    ? Number(afterOriginal?.order ?? afterOriginal?.number_point ?? afterOriginalIndex + 1) + 0.5
    : beforeOriginal
      ? Number(beforeOriginal?.order ?? beforeOriginal?.number_point ?? beforeOriginalArrayIndex + 1)
      : indexedBeforeOriginal
        ? Number(indexedBeforeOriginal?.order ?? indexedBeforeOriginal?.number_point ?? indexedBeforeOriginalIndex + 1)
        : insertion?.to_point_index === null
          ? 1
          : index + 1;
  const order = afterOriginal || beforeOriginal || indexedBeforeOriginal
    ? insertionOrder
    : Number(row?.number_point ?? row?.order ?? insertionOrder) || insertionOrder;
  const isDriving = String(routeType).toLowerCase() === "driving";
  const routeGroup = String(row?.route_group ?? row?.routeGroup ?? "main");
  const routeGroupName = String(
    row?.route_group_name ?? row?.routeGroupName ??
    (isDriving ? buildDrivingRouteGroupName(order, routeGroup) : ""),
  );
  const radiusDefault = isDriving ? 40 : 12;
  const turnCase = Number(row?.turn_case ?? row?.turnCase ?? 0);
  return {
    id: String(row?.id || `point-${order}`),
    order,
    number_point: order,
    latitude: Number(row?.latitude ?? row?.lat ?? 0),
    longitude: Number(row?.longitude ?? row?.lng ?? 0),
    name: { en: String(row?.name || "Direction") },
    description: { en: String(row?.description || "") },
    more_info: { en: String(row?.more_info || "") },
    notes: String(row?.notes || ""),
    radiusMeters: Number(row?.radius_meters ?? row?.radiusMeters ?? radiusDefault) || radiusDefault,
    turnCase,
    pointType: String(row?.point_type ?? row?.pointType ?? ""),
    routeGroup,
    routeGroupName,
    ...(isDriving
      ? { labelCode: buildDrivingLabelCode({ order, routeGroup, routeGroupName }, order - 1) }
      : {}),
    routeGroupParentPointId: "",
    images: Array.isArray(row?.images) ? row.images : [],
    audios: Array.isArray(row?.audios) ? row.audios : [],
    multimedia: [],
    aiAudioDescription: {},
    isPersisted: false,
    isGeneratedDirection: false,
    isAiGeneratedPoint: true,
    generatedForPointId: String(
      row?.generated_for_point_id || insertion?.from_point_id || insertion?.to_point_id || "",
    ),
    insertBetween: insertion,
  };
}

export function mergeAutomatedRoutePoints(originalPoints, responsePoints, routeType) {
  if (!Array.isArray(responsePoints)) {
    throw new Error("The AI response does not contain a points list.");
  }
  const originals = Array.isArray(originalPoints) ? originalPoints : [];
  const hasIdBasedInsertion = responsePoints.some((row) =>
    row?.new_point === true &&
    (String(row?.insert_between?.from_point_id || "").trim() ||
      String(row?.insert_between?.to_point_id || "").trim()),
  );
  const originalsById = new Map(originals.map((point) => [String(point?.id || ""), point]));
  const seen = new Set();
  const merged = responsePoints.map((row, index) => {
    const id = String(row?.id || "");
    if (row?.new_point === true) {
      if (!["walking", "driving"].includes(String(routeType).toLowerCase()) || row?.error === true) return null;
      return toGeneratedRoutePoint(row, index, originals, routeType);
    }
    const original = originalsById.get(id);
    if (!original || seen.has(id)) return null;
    seen.add(id);
    const order = Number(row?.number_point ?? row?.order ?? original?.order ?? original?.number_point) || 0;
    if (row?.error === true || String(row?.status || "").toLowerCase() === "error") {
      return {
        ...original,
        order,
        number_point: order,
        latitude: Number(row?.latitude ?? row?.lat ?? original?.latitude ?? 0),
        longitude: Number(row?.longitude ?? row?.lng ?? original?.longitude ?? 0),
        routeGroup: String(row?.route_group ?? row?.routeGroup ?? original?.routeGroup ?? "main"),
        routeGroupName: String(row?.route_group_name ?? row?.routeGroupName ?? original?.routeGroupName ?? ""),
      };
    }
    return {
      ...original,
      name: localizedEnglishField(original.name, String(row?.name ?? readEnglishValue(original.name))),
      description: localizedEnglishField(original.description, String(row?.description ?? readEnglishValue(original.description))),
      more_info: localizedEnglishField(original.more_info, String(row?.more_info ?? readEnglishValue(original.more_info))),
      order,
      number_point: order,
      latitude: Number(row?.latitude ?? row?.lat ?? original?.latitude ?? 0),
      longitude: Number(row?.longitude ?? row?.lng ?? original?.longitude ?? 0),
      turnCase: Number(row?.turn_case ?? row?.turnCase ?? original?.turnCase ?? 0),
      radiusMeters: Number(row?.radius_meters ?? row?.radiusMeters ?? original?.radiusMeters ?? 12),
      routeGroup: String(row?.route_group ?? row?.routeGroup ?? original?.routeGroup ?? "main"),
      routeGroupName: String(row?.route_group_name ?? row?.routeGroupName ?? original?.routeGroupName ?? ""),
      multimedia: mergeGeneratedImages(original, row) ?? original?.multimedia,
    };
  }).filter(Boolean);

  originals.forEach((point) => {
    if (!seen.has(String(point?.id || ""))) merged.push(point);
  });

  return merged
    .map((point, index) => ({ point, index }))
    .sort((first, second) => {
      const firstOrder = Number(first.point?.order ?? first.point?.number_point ?? 0);
      const secondOrder = Number(second.point?.order ?? second.point?.number_point ?? 0);
      return firstOrder - secondOrder || first.index - second.index;
    })
    .map(({ point }, index) => {
      const order = hasIdBasedInsertion
        ? index + 1
        : Number(point?.order ?? point?.number_point ?? index + 1) || index + 1;
      if (String(routeType).toLowerCase() !== "driving") return { ...point, order };
      const routeGroup = normalizeDrivingRouteGroupKey(
        point?.routeGroup ?? point?.route_group ?? "main",
        point?.routeGroupName ?? point?.route_group_name ?? "",
        order,
      );
      const routeGroupName = routeGroup === "main"
        ? buildDrivingRouteGroupName(order, routeGroup)
        : String(point?.routeGroupName ?? point?.route_group_name ?? buildDrivingRouteGroupName(order, routeGroup));
      return {
        ...point,
        order,
        number_point: order,
        routeGroup,
        routeGroupName,
        labelCode: routeGroup === "main"
          ? buildDrivingLabelCode({ order, routeGroup, routeGroupName }, order - 1)
          : String(point?.labelCode ?? point?.label_code ?? buildDrivingLabelCode({ order, routeGroup, routeGroupName }, order - 1)),
      };
    });
}

export function getAutomationPointResults(originalPoints, responsePoints, mergedPoints = []) {
  const pointsById = new Map((Array.isArray(originalPoints) ? originalPoints : [])
    .map((point) => [String(point?.id || ""), point]));
  const mergedPointsById = new Map((Array.isArray(mergedPoints) ? mergedPoints : [])
    .map((point) => [String(point?.id || ""), point]));
  return (Array.isArray(responsePoints) ? responsePoints : []).reduce((result, row) => {
    if (row?.new_point === true) {
      if (row?.error === true) {
        result.failed.push({ id: String(row?.id || "unknown-new-point"), label: `New point (${String(row?.id || "unknown ID")})` });
        return result;
      }
      const id = String(row?.id || `new-${result.created.length}`);
      const mergedPoint = mergedPointsById.get(id) || {};
      const order = Number(mergedPoint?.order ?? mergedPoint?.number_point ?? row?.number_point) || 0;
      result.created.push({
        id,
        order,
        labelCode: String(mergedPoint?.labelCode ?? mergedPoint?.label_code ?? ""),
        label: `Point ${order || "?"}`,
      });
      return result;
    }
    const id = String(row?.id || "");
    const point = pointsById.get(id);
    const localizedName = readEnglishValue(point?.name);
    const label = `#${point?.number_point ?? point?.order ?? "?"}${localizedName ? ` — ${localizedName}` : ` (${id || "unknown ID"})`}`;
    (row?.error === true || String(row?.status || "").toLowerCase() === "error" || !point
      ? result.failed
      : result.succeeded).push({
      id,
      label: point ? label : `${label} — unknown point returned by AI`,
    });
    return result;
  }, { succeeded: [], failed: [], created: [] });
}
